import { Button, Empty, Flex, Skeleton, Spin, Typography } from "antd"
import { useCallbackRef } from "../../../hooks/use-callback-ref.ts"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { type RootState } from "../../../store"
import { NotificationSettingsForm } from "../../Notifications/components/NotificationSettingsForm"
import {
  ManageChannels,
  type SmsChannelState,
  smsNeedsSetup
} from "../../Notifications/components/settings/ManageChannels.tsx"
import { useDesktopPermission } from "../../Notifications/hooks/use-desktop-permission.ts"
import {
  fetchNotificationSettings,
  updateNotificationSettings
} from "../../Notifications/redux/notifications.thunks"
import { useSmsVerification } from "../../Notifications/sms/use-sms-verification.ts"
import type {
  NotificationSettings,
  NotificationSettingsItem
} from "../../Notifications/types/notification.types"

const { Text, Title } = Typography

// CALENDAR moved to Calendar Settings > Notifications, which reads and
// writes this exact same group through the same thunks/endpoint — one
// source of truth, shown in one place. Hiding it here only drops it from
// this page's render; a save from here still can't touch it, since the form
// only sends the rows it was given.
const GLOBAL_PAGE_EXCLUDED_GROUPS = ["CALENDAR"]

const forGlobalPage = (
  settings: NotificationSettings | null
): NotificationSettings | null => {
  if (!settings) return settings
  return Object.fromEntries(
    Object.entries(settings).filter(
      ([key]) => !GLOBAL_PAGE_EXCLUDED_GROUPS.includes(key)
    )
  )
}

const SettingsNotificationPage = ({
  settings,
  settingsError,
  getSettings,
  isUpdating,
  updateSettings
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const sms = useSmsVerification()
  const data = useMemo(() => forGlobalPage(settings), [settings])
  const desktop = useDesktopPermission(data)
  const [channelsOpen, setChannelsOpen] = useState(false)

  const getSettingsLatest = useCallbackRef(getSettings)
  useEffect(() => {
    getSettingsLatest()
  }, [getSettingsLatest])

  const handleFinish = (values: {
    [key: string]: Partial<NotificationSettingsItem>
  }) => {
    // Backend 500s on a partial row (reads all channel fields with no
    // default); the form sends every channel a row carries, so spreading
    // `value` always sends the full row.
    const mapValues = Object.entries(values).map(([key, value]) => ({
      ...value,
      notification_type: Number(key)
    })) as NotificationSettingsItem[]
    updateSettings(mapValues)
  }

  useEffect(() => {
    generateDocumentTitle("Settings Notifications")
  }, [])

  const smsChannel: SmsChannelState = {
    offered: Object.values(data ?? {})
      .flat()
      .some((item) => item.sms_available === true),
    state: sms.state,
    lock: sms.lock,
    failed: sms.failed,
    onRetry: () => void sms.reload(),
    onVerified: (verified) => {
      sms.setState(verified)
      getSettings()
    }
  }

  return (
    <Flex vertical gap={16} flex={1} className="min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Title level={3} className="!mb-1 text-start">
            {t("notifications.settings.title")}
          </Title>
          <Text type="secondary">{t("notifications.settings.subtitle")}</Text>
        </div>
        {data && (
          <ManageChannels
            desktop={desktop}
            sms={smsChannel}
            open={channelsOpen}
            onOpenChange={setChannelsOpen}
          />
        )}
      </div>
      {data ? (
        <NotificationSettingsForm
          settings={data}
          onFinish={handleFinish}
          saving={isUpdating}
          smsLock={sms.lock}
          onDesktopTurnedOn={desktop.onDesktopTurnedOn}
          onSmsSetup={
            smsNeedsSetup(smsChannel) ? () => setChannelsOpen(true) : undefined
          }
        />
      ) : settingsError ? (
        <Flex flex={1} align="center" justify="center">
          <Empty description={t("notifications.settings.loadError")}>
            <Button onClick={() => getSettings()}>
              {t("notifications.settings.retry")}
            </Button>
          </Empty>
        </Flex>
      ) : (
        <Flex flex={1}>
          <Spin fullscreen size="large" />
          <Skeleton.Node className={"size-full"} />
        </Flex>
      )}
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  settings: state.notification.notificationSettings,
  fetching: state.notification.fetchingSettings,
  settingsError: state.notification.settingsError,
  isUpdating: state.notification.isUpdatingSettings
})

const mapDispatchToProps = {
  getSettings: fetchNotificationSettings,
  updateSettings: updateNotificationSettings
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(SettingsNotificationPage)
